import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { EmojiEvents, Speed, Handshake } from '@mui/icons-material';
import apiService from '../services/apiService';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import SearchableSelect from './dashboard/components/SearchableSelect';

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
];

const AgentPerformance = () => {
  useAuth(); // ensure authenticated
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);

  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agents, setAgents] = useState([]);

  const [sortBy, setSortBy] = useState('totalCalls');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedAgent) params.append('agentId', selectedAgent);

      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/agent-performance-report?${params.toString()}`
      );
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      console.error('Error fetching agent performance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/collections-officers`
      );
      const data = await response.json();
      setAgents(data.collectionsOfficers || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    if (reportData?.isManager) {
      fetchAgents();
    }
  }, [reportData?.isManager]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const sortAgentStats = (stats) => {
    if (!stats || stats.length === 0) return [];
    return [...stats].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const { leaderboard, agentStats, promiseFunnel, workloadDistribution } = reportData;

  // Workload distribution doughnut
  const workloadData = {
    labels: workloadDistribution.map(w => w.agentName),
    datasets: [{
      data: workloadDistribution.map(w => w.loanCount),
      backgroundColor: workloadDistribution.map((_, i) => COLORS[i % COLORS.length]),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const workloadOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, font: { size: 12 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = workloadDistribution.reduce((s, w) => s + w.loanCount, 0);
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} loans (${pct}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Agent Performance & Productivity</h1>
          <p className="text-gray-600 mt-1">Track agent collection performance and workload</p>
        </div>

        {/* Filters */}
        <div className="mb-8 bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            {reportData?.isManager && (
              <div>
                <SearchableSelect
                  options={agents}
                  value={selectedAgent}
                  onChange={setSelectedAgent}
                  placeholder="All Agents"
                  label="Filter by Agent"
                  getOptionLabel={(agent) => `${agent.firstName} ${agent.lastName}`}
                  getOptionValue={(agent) => agent._id}
                />
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => {
                setStartDate(new Date().toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
                setSelectedAgent('');
                setTimeout(() => fetchReport(), 0);
              }}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear Filters
            </button>
            <button
              onClick={() => fetchReport()}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </button>
          </div>
        </div>

        {/* Leaderboard Cards */}
        {reportData?.isManager && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-yellow-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Top Collector</p>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {leaderboard.topCollector?.agentName || 'N/A'}
                  </p>
                  <p className="text-sm text-yellow-600 mt-1">
                    {leaderboard.topCollector ? formatCurrency(leaderboard.topCollector.value) : '--'}
                  </p>
                </div>
                <div className="bg-yellow-100 p-3 rounded-full">
                  <EmojiEvents className="text-yellow-600 w-6 h-6" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-green-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Best Reach Rate</p>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {leaderboard.bestReachRate?.agentName || 'N/A'}
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    {leaderboard.bestReachRate ? `${leaderboard.bestReachRate.value}%` : '--'}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Speed className="text-green-600 w-6 h-6" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-blue-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Best PTP Conversion</p>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {leaderboard.bestPtpConversion?.agentName || 'N/A'}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    {leaderboard.bestPtpConversion ? `${leaderboard.bestPtpConversion.value}%` : '--'}
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <Handshake className="text-blue-600 w-6 h-6" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agent Comparison Table */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Comparison</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {[
                    { key: 'agentName', label: 'Agent' },
                    { key: 'totalCalls', label: 'Total Calls' },
                    { key: 'clientsReached', label: 'Reached' },
                    { key: 'reachRate', label: 'Reach %' },
                    { key: 'promisesCount', label: 'Promises' },
                    { key: 'promiseAmount', label: 'Promise Amt' },
                    { key: 'paymentsCollected', label: 'Payments' },
                    { key: 'paymentAmount', label: 'Payment Amt' },
                    { key: 'ptpConversionRate', label: 'PTP Conv %' }
                  ].map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={`${col.key === 'agentName' ? 'text-left' : 'text-right'} py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:text-cyan-600`}
                    >
                      {col.label} {sortBy === col.key && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortAgentStats(agentStats).map((agent, index) => (
                  <tr key={agent.agentId || index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{agent.agentName}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{agent.totalCalls}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{agent.clientsReached}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        agent.reachRate >= 70 ? 'text-green-600' :
                        agent.reachRate >= 40 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {agent.reachRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{agent.promisesCount}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{formatCurrency(agent.promiseAmount)}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{agent.paymentsCollected}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-700">{formatCurrency(agent.paymentAmount)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        agent.ptpConversionRate >= 50 ? 'text-green-600' :
                        agent.ptpConversionRate >= 25 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {agent.ptpConversionRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {agentStats.length === 0 && (
              <div className="text-center py-8 text-gray-500">No agent data for the selected period</div>
            )}
          </div>
        </div>

        {/* Bottom Row: Workload + Promise Funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Workload Distribution */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Workload Distribution</h3>
            <div style={{ height: 300 }}>
              {workloadDistribution.length > 0 ? (
                <Doughnut data={workloadData} options={workloadOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">No workload data</div>
              )}
            </div>
          </div>

          {/* Promise Funnel */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Promise Fulfillment Funnel</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Agent</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">Pending</span>
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">Fulfilled</span>
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">Partial</span>
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800">Broken</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {promiseFunnel.map((agent, index) => (
                    <tr key={agent.agentId || index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{agent.agentName}</td>
                      <td className="py-3 px-4 text-right text-sm text-yellow-700 font-medium">{agent.pending}</td>
                      <td className="py-3 px-4 text-right text-sm text-green-700 font-medium">{agent.fulfilled}</td>
                      <td className="py-3 px-4 text-right text-sm text-blue-700 font-medium">{agent.partially_fulfilled}</td>
                      <td className="py-3 px-4 text-right text-sm text-red-700 font-medium">{agent.broken}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {promiseFunnel.length === 0 && (
                <div className="text-center py-8 text-gray-500">No promise data for the selected period</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPerformance;
